import type {
    ComponentPropsWithRef,
    ReactNode,
    TableHTMLAttributes,
    ThHTMLAttributes,
    TdHTMLAttributes,
    HTMLAttributes,
} from 'react'

export interface TableContainerProps extends Omit<
    TableHTMLAttributes<HTMLTableElement>,
    'title'
> {
    isEmpty?: boolean
    fullWidth?: boolean
    emptyState?: ReactNode
    children?: ReactNode
}

export function TableContainer({
    isEmpty = false,
    fullWidth = true,
    emptyState,
    children,
    className = '',
    ...tableProps
}: TableContainerProps) {
    const fullWidthClass = fullWidth ? 'min-w-full' : ''

    return (
        <div className="flex flex-col">
            <div className="-m-1.5 overflow-x-auto">
                <div
                    className={`p-1.5 inline-block align-middle ${fullWidthClass}`.trim()}
                >
                    <div className="border border-stone-200 shadow-xs rounded-lg2 overflow-hidden">
                        <table
                            {...tableProps}
                            className={`divide-y divide-gray-200 ${fullWidthClass} ${className}`.trim()}
                        >
                            {children}
                        </table>
                        {isEmpty && emptyState ? emptyState : null}
                    </div>
                </div>
            </div>
        </div>
    )
}

export function TableHead({
    children,
    className = '',
    ...props
}: HTMLAttributes<HTMLTableSectionElement>) {
    return (
        <thead
            {...props}
            className={`bg-gray-50 ${className}`.trim()}
        >
            <tr>{children}</tr>
        </thead>
    )
}

export function TableHeadCell({
    children,
    className = '',
    ...props
}: ThHTMLAttributes<HTMLTableCellElement>) {
    return (
        <th
            scope="col"
            {...props}
            className={`px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase whitespace-nowrap ${className}`.trim()}
        >
            {children}
        </th>
    )
}

export function TableBody({
    children,
    className = '',
    ...props
}: HTMLAttributes<HTMLTableSectionElement>) {
    return (
        <tbody
            {...props}
            className={`divide-y divide-gray-200 ${className}`.trim()}
        >
            {children}
        </tbody>
    )
}

export function TableRow({
    children,
    className = '',
    ...props
}: HTMLAttributes<HTMLTableRowElement>) {
    return (
        <tr
            {...props}
            className={`hover:bg-gray-100 ${className}`.trim()}
        >
            {children}
        </tr>
    )
}

export function TableCell({
    children,
    className = '',
    ...props
}: TdHTMLAttributes<HTMLTableCellElement>) {
    return (
        <td
            {...props}
            className={`px-4 py-3 whitespace-nowrap text-sm bg-white text-gray-800 ${className}`.trim()}
        >
            {children}
        </td>
    )
}

export interface TableEmptyStateProps extends Omit<
    ComponentPropsWithRef<'div'>,
    'title'
> {
    title?: ReactNode
    subtitle?: ReactNode
    action?: ReactNode
    children?: ReactNode
}

export function TableEmptyState({
    title,
    subtitle,
    action,
    children,
    className = '',
    ...restProps
}: TableEmptyStateProps) {
    return (
        <div
            {...restProps}
            className={`py-12 bg-white ${className}`.trim()}
        >
            <div className="flex flex-col justify-center items-center space-y-4">
                <div className="text-center">
                    {title ? (
                        <div className="font-medium text-gray-700">{title}</div>
                    ) : null}
                    {subtitle ? (
                        <div className="text-sm text-gray-600 mt-1">
                            {subtitle}
                        </div>
                    ) : null}
                </div>
                {action ? <div>{action}</div> : null}
                {children}
            </div>
        </div>
    )
}

export const Table = Object.assign(TableContainer, {
    Container: TableContainer,
    Head: TableHead,
    HeadCell: TableHeadCell,
    Body: TableBody,
    Row: TableRow,
    Cell: TableCell,
    EmptyState: TableEmptyState,
})
